import {Card, createStyles, Group, Stack, Text, Title} from '@mantine/core';
import React from 'react';

import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {ANALYSIS_TEMPLATE_INTEGRATIONS} from './CompatibilityCheckIntegrationRegistry';
import {
  AnalysisTemplateIntegration,
  CheckResultLevel,
  CompatibilityCheckResult,
  getTemplateVariablesByType,
  TemplateVariablesByType,
} from './CompatibilityCheckTypes';

const CHECK_RESULT_ICON: Record<CheckResultLevel, BenchlingIconName> = {
  [CheckResultLevel.PASS]: 'check-circle',
  [CheckResultLevel.FAIL]: 'circle-xmark',
  [CheckResultLevel.WARN]: 'exclamation-triangle',
};

const CHECK_RESULT_ALT_TEXT: Record<CheckResultLevel, string> = {
  [CheckResultLevel.PASS]: 'Passes',
  [CheckResultLevel.FAIL]: 'Fails',
  [CheckResultLevel.WARN]: 'Warning',
};

const useStyles = createStyles((theme) => ({
  [CheckResultLevel.PASS]: {color: theme.colors.green[7]},
  [CheckResultLevel.FAIL]: {color: theme.colors.red[7]},
  [CheckResultLevel.WARN]: {color: theme.colors.yellow[7]},
}));

function CompatibilityCheckResultRow({result}: {result: CompatibilityCheckResult}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Group
      spacing="xs"
      noWrap={true}
      {...getTestProps({component: 'CompatibilityCheckResultRow', element: result.level})}
    >
      <BenchlingIcon
        icon={CHECK_RESULT_ICON[result.level]}
        className={classes[result.level]}
        altText={CHECK_RESULT_ALT_TEXT[result.level]}
      />
      <Text>{result.message}</Text>
    </Group>
  );
}

function AnalysisTemplateIntegrationSection({
  integration,
  variables,
}: {
  integration: AnalysisTemplateIntegration;
  variables: TemplateVariablesByType;
}): JSX.Element {
  const results = integration.checks
    .map((check) => check(variables))
    .filter((result): result is CompatibilityCheckResult => result != null);

  return (
    <Card
      withBorder={true}
      padding="sm"
      radius="sm"
      {...getTestProps({component: 'AnalysisTemplateIntegrationSection', element: integration.name})}
    >
      <Stack spacing="xs">
        <Group spacing="sm" noWrap={true}>
          <BenchlingIcon icon={integration.icon} />
          <Title order={5}>{integration.name}</Title>
        </Group>
        <Stack spacing="xs">
          {results.map((result, index) => (
            <CompatibilityCheckResultRow key={index} result={result} />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

/** @package */
export interface CompatibilityCheckTabProps {
  template: Pick<AnalysisTemplateGraphQL, 'id' | 'steps' | 'configVariables' | 'dataFrameVariables'>;
}

/**
 * Displays which integrations an analysis template's inputs are compatible with.
 *
 * To add a new integration, register the integration in `CompatibilityCheckIntegrationRegistry.ts`.
 *
 * @package
 */
export default function CompatibilityCheckTab({template}: CompatibilityCheckTabProps): JSX.Element {
  const visibleIntegrations = ANALYSIS_TEMPLATE_INTEGRATIONS.filter((integration) => integration.visible());

  const variables = getTemplateVariablesByType(template);

  return (
    <Stack spacing="md" {...getTestProps({component: 'CompatibilityCheckTab', element: 'root'})}>
      {visibleIntegrations.length > 0 ? (
        visibleIntegrations.map((integration) => (
          <AnalysisTemplateIntegrationSection
            key={integration.name}
            integration={integration}
            variables={variables}
          />
        ))
      ) : (
        <Text size="sm" color="dimmed">
          No integrations are enabled.
        </Text>
      )}
    </Stack>
  );
}
